import { RefObject, useEffect, useRef, useState } from 'react';

type UseCanvasOverlayOpts = {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  canvasVersion?: number;
  zIndex?: number;
};

export const useCanvasOverlay = ({ canvasRef, canvasVersion = 0, zIndex = 0 }: UseCanvasOverlayOpts) => {
  const overlayRef = useRef<HTMLCanvasElement | null>(document.createElement('canvas'));
  const [layerVersion, setLayerVersion] = useState(0);

  useEffect(() => {
    if (!canvasVersion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const parentElement = canvas.parentElement;
    if (!parentElement) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    overlay.style.position = 'absolute';
    overlay.style.pointerEvents = 'auto';
    overlay.style.zIndex = zIndex.toString();

    if (window.getComputedStyle(parentElement).position === 'relative') {
      overlay.style.top = '0px';
      overlay.style.left = '0px';
    }

    let frameId: number | null = null;

    const updateOverlay = () => {
      if (frameId != null) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const context = overlay.getContext('2d', { willReadFrequently: true });
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        const newWidth = Math.round(rect.width * dpr);
        const newHeight = Math.round(rect.height * dpr);

        if (context && (overlay.width !== newWidth || overlay.height !== newHeight)) {
          overlay.style.width = `${rect.width}px`;
          overlay.style.height = `${rect.height}px`;
          overlay.width = newWidth;
          overlay.height = newHeight;

          context.setTransform(dpr, 0, 0, dpr, 0, 0);

          setLayerVersion((v) => v + 1);
        }
      });
    };

    updateOverlay();

    const resizeObserver = new ResizeObserver(updateOverlay);
    resizeObserver.observe(canvas);
    window.addEventListener('resize', updateOverlay);

    parentElement.appendChild(overlay);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateOverlay);
      overlay.remove();
      if (frameId != null) cancelAnimationFrame(frameId);
    };
  }, [canvasRef, canvasVersion, zIndex]);

  return { overlayRef, layerVersion };
};
